import { useEffect, useState } from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { useAppSelector } from "app/hooks.ts";
import { selectUser } from "features/auth/authSlice.ts";
import { getPermissionModuleUsageForPath } from "features/permissions/permissionModuleInspector.ts";
import {
  isPermissionInspectorEnabled,
  PERMISSION_INSPECTOR_TOGGLE_EVENT,
} from "features/permissions/permissionInspectorToggle.ts";
import { selectPermissions } from "features/permissions/permissionsSlice.ts";
import type { PermissionModuleUsage } from "features/permissions/permissionModuleInspector.ts";
import type { PermissionsParsed } from "features/permissions/permissionsTypes.ts";

type InspectorState = {
  clickedLabel: string;
  modules: PermissionModuleUsage[];
  path: string;
};

const formatValue = (value: boolean | undefined) => (value ? "Yes" : "No");

const resolveClickedLabel = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) {
    return "Unknown area";
  }

  const labelSource =
    target.closest("[aria-label]")?.getAttribute("aria-label") ||
    target.closest("button,a,[role='tab'],[role='menuitem']")?.textContent ||
    target.textContent;

  return labelSource?.trim() || "Page surface";
};

export const SysAdminPermissionInspector = () => {
  const user = useAppSelector(selectUser);
  const permissions = useAppSelector(selectPermissions);
  const [inspectorState, setInspectorState] = useState<InspectorState | null>(
    null,
  );
  const [isInspectorEnabled, setIsInspectorEnabled] = useState(
    isPermissionInspectorEnabled,
  );

  const isSysAdmin = user?.role?.toLowerCase() === "sysadmin";

  useEffect(() => {
    const syncToggleState = () => {
      setIsInspectorEnabled(isPermissionInspectorEnabled());
    };

    window.addEventListener(PERMISSION_INSPECTOR_TOGGLE_EVENT, syncToggleState);
    window.addEventListener("storage", syncToggleState);

    return () => {
      window.removeEventListener(
        PERMISSION_INSPECTOR_TOGGLE_EVENT,
        syncToggleState,
      );
      window.removeEventListener("storage", syncToggleState);
    };
  }, []);

  useEffect(() => {
    if (!isSysAdmin || !isInspectorEnabled) {
      setInspectorState(null);
      return;
    }

    const onCtrlClick = (event: MouseEvent) => {
      if (!event.ctrlKey || event.button !== 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const path = window.location.pathname;
      setInspectorState({
        clickedLabel: resolveClickedLabel(event.target),
        modules: getPermissionModuleUsageForPath(path),
        path,
      });
    };

    document.addEventListener("click", onCtrlClick, true);

    return () => {
      document.removeEventListener("click", onCtrlClick, true);
    };
  }, [isInspectorEnabled, isSysAdmin]);

  const hasVisiblePanel = Boolean(isSysAdmin && isInspectorEnabled && inspectorState);

  if (!hasVisiblePanel || !inspectorState) {
    return null;
  }

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        bottom: 24,
        boxShadow: 10,
        maxHeight: "70vh",
        overflowY: "auto",
        p: 2,
        position: "fixed",
        right: 16,
        width: 460,
        zIndex: 1300,
      }}
    >
      <Stack direction="row" justifyContent="space-between" mb={1}>
        <Typography fontWeight={700}>Page module inspector</Typography>
        <Button onClick={() => setInspectorState(null)} size="small" variant="text">
          Close
        </Button>
      </Stack>
      <Typography variant="body2">Path: {inspectorState.path}</Typography>
      <Typography mb={1} variant="body2">
        Clicked: {inspectorState.clickedLabel}
      </Typography>

      {inspectorState.modules.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          No permission modules were detected yet for this page. Try opening a feature section first and Ctrl+click again.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {inspectorState.modules.map((moduleUsage) => {
            const moduleKey = moduleUsage.moduleName as keyof PermissionsParsed;
            const userPermission = permissions?.[moduleKey];

            return (
              <Box
                key={moduleUsage.moduleName}
                sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1 }}
              >
                <Typography fontWeight={700}>{moduleUsage.moduleName}</Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.75} mt={0.75}>
                  <Chip label={`Read: ${formatValue(userPermission?.isRead)}`} size="small" />
                  <Chip
                    label={`Create: ${formatValue(userPermission?.isCreate)}`}
                    size="small"
                  />
                  <Chip
                    label={`Update: ${formatValue(userPermission?.isUpdate)}`}
                    size="small"
                  />
                  <Chip
                    label={`Delete: ${formatValue(userPermission?.isDelete)}`}
                    size="small"
                  />
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default SysAdminPermissionInspector;
